import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { useProjects } from '../../contexts/ProjectContext';
import { useContextEngine } from '../../contexts/ContextEngineContext';
import { SignalCategory } from '../../types/context.types';
import ProjectBrainModal from './ProjectBrainModal';
import './ProjectBrainDialog.css';

const ProjectBrainDialog: React.FC = () => {
  const location = useLocation();
  const {
    showNewProjectDialog,
    newProjectDialogOptions,
    closeNewProjectDialog,
    createProject,
    selectProject,
  } = useProjects();
  const { processOnboarding, processingContext } = useContextEngine();
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null);

  const isOnboarding = location.pathname === '/onboarding';
  const show = showNewProjectDialog && !isOnboarding;

  useEffect(() => {
    if (!show) {
      setCreatedProjectId(null);
    }
  }, [show]);

  const ensureProject = async (projectName: string): Promise<string | null> => {
    if (createdProjectId) return createdProjectId;
    try {
      const newProject = await createProject({ name: projectName });
      selectProject(newProject);
      newProjectDialogOptions.onCreated?.(newProject);
      setCreatedProjectId(newProject.id);
      return newProject.id;
    } catch (err: any) {
      if (err?.response?.status === 403) {
        const onLimitError = newProjectDialogOptions.onLimitError;
        closeNewProjectDialog();
        onLimitError?.();
      } else {
        console.error('Error creating project:', err);
      }
      throw err;
    }
  };

  const handleComplete = async (result: {
    projectName: string;
    files: Array<{ fileName: string; fileType: string; fileSize: number; data: string }>;
    manualTags: Array<{ label: string; category: SignalCategory }>;
  }) => {
    const projectId = await ensureProject(result.projectName);
    if (!projectId) {
      throw new Error('Project creation failed');
    }
    const onboardingResult = await processOnboarding(projectId, result.files, result.manualTags);
    return {
      sources: onboardingResult.sources,
      signals: onboardingResult.signals,
      readiness: onboardingResult.readiness,
    };
  };

  const handleContinue = async (projectName: string) => {
    try {
      await ensureProject(projectName);
    } catch {
      return;
    }
    closeNewProjectDialog();
  };

  return (
    <Modal show={show} onHide={closeNewProjectDialog} centered backdrop="static" className="project-brain-dialog">
      <Modal.Body>
        <ProjectBrainModal
          onComplete={handleComplete}
          onContinue={handleContinue}
          processing={processingContext}
          continueButtonText="Done"
          showProjectName
        />
      </Modal.Body>
    </Modal>
  );
};

export default ProjectBrainDialog;
